import { Controller } from "@hotwired/stimulus"
import mapboxgl from 'mapbox-gl'

const STATUS_COLORS = {
  green: "#56BC9E",
  yellow: "#E6B16B",
  red: "#E2848B",
  none: "#9CA3AF"
}

// Connects to data-controller="access-report-map"
export default class extends Controller {
  static values = {
    accessToken: String,
    geojson: String,
    latitude: Number,
    longitude: Number,
    immersive: Boolean
  }
  static targets = ["map", "popupTemplate"]

  connect() {
    if (!this.hasMapTarget) return

    mapboxgl.accessToken = this.accessTokenValue

    try {
      this.map = new mapboxgl.Map({
        container: this.mapTarget,
        style: "mapbox://styles/mapbox/light-v11",
        center: [this.longitudeValue || -83.7487, this.latitudeValue || 42.2658],
        zoom: 10,
        attributionControl: this.immersiveValue ? { compact: true } : false,
        cooperativeGestures: this.immersiveValue
      })

      if (this.immersiveValue) {
        this.map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), "top-right")
        this.map.setPadding(this.canvasPadding())
        this.resizeObserver = new ResizeObserver(() => {
          this.map.resize()
          this.map.setPadding(this.canvasPadding())
        })
        this.resizeObserver.observe(this.element)
      }
      this.map.on("load", () => this.onMapLoad())
    } catch (error) {
      console.error("Mapbox initialization failed:", error)
    }

    // Listen for provider selection from access-report controller
    this.boundOnProviderSelected = (e) => {
      const { npi, lat, lng } = e.detail
      this.highlightProvider(npi)
      if (lat && lng && this.map) {
        this.map.easeTo({ center: [lng, lat], zoom: Math.max(this.map.getZoom(), 13) })
      }
    }
    document.addEventListener("access-report:providerSelected", this.boundOnProviderSelected)
    this.boundOnProviderSelectionCleared = () => {
      this.clearHighlight()
      this.popup?.remove()
    }
    document.addEventListener("access-report:providerSelectionCleared", this.boundOnProviderSelectionCleared)

    // Listen for type filter changes from chip clicks
    this.boundOnTypeFilterChanged = (e) => {
      this.filterByType(e.detail.type)
    }
    document.addEventListener("access-report:typeFilterChanged", this.boundOnTypeFilterChanged)
  }

  onMapLoad() {
    if (!this.geojsonValue) return

    let geojson
    try {
      geojson = JSON.parse(this.geojsonValue)
    } catch (e) {
      console.error("Failed to parse GeoJSON:", e)
      return
    }

    if (!geojson || !geojson.features || geojson.features.length === 0) return

    if (this.immersiveValue) {
      const bounds = new mapboxgl.LngLatBounds()
      geojson.features.forEach(feature => bounds.extend(feature.geometry.coordinates))
      this.map.fitBounds(bounds, { padding: this.canvasPadding(), maxZoom: 12, duration: 0 })
    }

    // Add GeoJSON source — no clustering, show all individual pins
    this.map.addSource("providers", {
      type: "geojson",
      data: geojson
    })

    // Individual provider dots
    this.map.addLayer({
      id: "provider-points",
      type: "circle",
      source: "providers",
      paint: {
        "circle-color": [
          "match", ["get", "status"],
          "green", STATUS_COLORS.green,
          "yellow", STATUS_COLORS.yellow,
          "red", STATUS_COLORS.red,
          STATUS_COLORS.none
        ],
        "circle-radius": 5,
        "circle-stroke-width": 1,
        "circle-stroke-color": "#ffffff"
      }
    })

    // Click individual point
    this.map.on("click", "provider-points", (e) => {
      const npi = e.features[0].properties.id
      if (npi) {
        if (this.selectedNpi === String(npi)) {
          this.clearHighlight()
          this.popup?.remove()
          document.dispatchEvent(new CustomEvent("access-report-map:providerDeselected"))
        } else {
          this.highlightProvider(String(npi))
          document.dispatchEvent(new CustomEvent("access-report-map:providerClick", {
            detail: { npi: String(npi) }
          }))
        }
      }
    })

    // Hover flyout popup
    this.popup = new mapboxgl.Popup({
      closeButton: false,
      closeOnClick: false,
      offset: 12,
      maxWidth: "260px"
    })

    this.map.on("mouseenter", "provider-points", (e) => {
      this.map.getCanvas().style.cursor = "pointer"
      const props = e.features[0].properties
      const popupContent = this.popupTemplateTarget.content.firstElementChild.cloneNode(true)
      popupContent.querySelector("[data-popup-name]").textContent = props.name || "Unknown"
      popupContent.querySelector("[data-popup-specialty]").textContent = props.specialty || props.providerType || ""
      const status = STATUS_COLORS[props.status] ? props.status : "none"
      popupContent.querySelector(`[data-popup-risk="${status}"]`).classList.remove("hidden")
      for (const [attribute, value] of [["networked", props.networked], ["501r", props.is501r]]) {
        popupContent.querySelector(`[data-popup-${attribute}]`).classList.toggle("hidden", value !== true && value !== "true")
      }
      const groupCount = parseInt(props.groupCount, 10) || 1
      if (groupCount > 1) {
        const groupNote = popupContent.querySelector("[data-popup-group]")
        groupNote.textContent = `+${groupCount - 1} related NPI${groupCount > 2 ? "s" : ""} at this provider`
        groupNote.classList.remove("hidden")
      }
      this.popup.setLngLat(e.features[0].geometry.coordinates.slice()).setDOMContent(popupContent).addTo(this.map)
    })

    this.map.on("mouseleave", "provider-points", () => {
      this.map.getCanvas().style.cursor = ""
      this.popup.remove()
    })

    // Apply any pending type filter that arrived before map loaded
    if (this.pendingTypeFilter) {
      this.filterByType(this.pendingTypeFilter)
      this.pendingTypeFilter = null
    }
  }

  // Keep pins in the exposed area of the map, including when selecting a row.
  // The panel stacks below the map at the same breakpoint used by the CSS.
  canvasPadding() {
    const canvas = this.element.getBoundingClientRect()
    const overlay = this.element.closest(".ar-map-stage")?.querySelector(".ar-landscape-overlay")
    if (window.matchMedia("(min-width: 901px)").matches && overlay) {
      const panel = overlay.getBoundingClientRect()
      return { top: 90, bottom: 120, left: Math.max(40, panel.right - canvas.left + 44), right: 80 }
    }
    return { top: 60, bottom: 110, left: 40, right: 40 }
  }

  highlightProvider(npi) {
    if (!this.map || !this.map.getSource("providers")) return
    this.selectedNpi = String(npi)

    if (this.map.getLayer("highlight-point")) {
      this.map.setFilter("highlight-point", ["==", ["get", "id"], npi])
    } else {
      this.map.addLayer({
        id: "highlight-point",
        type: "circle",
        source: "providers",
        filter: ["==", ["get", "id"], npi],
        paint: {
          "circle-radius": 12,
          "circle-color": "transparent",
          "circle-stroke-width": 3,
          "circle-stroke-color": "#4C56E0"
        }
      })
    }
  }

  clearHighlight() {
    this.selectedNpi = null
    if (!this.map) return
    if (this.map.getLayer("highlight-point")) {
      this.map.setFilter("highlight-point", ["==", ["get", "id"], ""])
    }
  }

  filterByType(type) {
    if (!this.map || !this.map.getLayer("provider-points")) {
      this.pendingTypeFilter = type
      return
    }

    this.clearHighlight()

    if (type === "all") {
      this.map.setFilter("provider-points", null)
    } else {
      this.map.setFilter("provider-points", ["==", ["get", "providerType"], type])
    }
  }

  disconnect() {
    document.removeEventListener("access-report:providerSelected", this.boundOnProviderSelected)
    document.removeEventListener("access-report:providerSelectionCleared", this.boundOnProviderSelectionCleared)
    document.removeEventListener("access-report:typeFilterChanged", this.boundOnTypeFilterChanged)
    if (this.resizeObserver) this.resizeObserver.disconnect()
    if (this.popup) this.popup.remove()
    if (this.map) {
      this.map.remove()
      this.map = null
    }
  }
}
